/**
 * Face-edge kernel — small 3D/2D geometry helpers (K0).
 *
 * Deliberately self-contained (no imports from the kernel's 2D path stack): the
 * body kernel's math is its own world, and keeping it dependency-free keeps the
 * arrangement core testable in isolation and the import boundaries trivial.
 */

import type { BodyPt3, BodyPlane } from './types'

/** THE tolerance (spike §3.2, Matt Q6): 1/64 inch, the model's precision convention. */
export const WELD_TOL = 1 / 768 // ft
export const WELD_TOL_SQ = WELD_TOL * WELD_TOL
/** area epsilon for degenerate-cycle rejection: a tol-by-tol sliver */
export const AREA_EPS = WELD_TOL * WELD_TOL

export const v3 = (x: number, y: number, z: number): BodyPt3 => ({ x, y, z })
export const add = (a: BodyPt3, b: BodyPt3): BodyPt3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z })
export const sub = (a: BodyPt3, b: BodyPt3): BodyPt3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z })
export const scale = (a: BodyPt3, s: number): BodyPt3 => ({ x: a.x * s, y: a.y * s, z: a.z * s })
export const dot = (a: BodyPt3, b: BodyPt3): number => a.x * b.x + a.y * b.y + a.z * b.z
export const cross = (a: BodyPt3, b: BodyPt3): BodyPt3 => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
})
export const len = (a: BodyPt3): number => Math.sqrt(dot(a, a))
export const distSq = (a: BodyPt3, b: BodyPt3): number => {
  const dx = a.x - b.x, dy = a.y - b.y, dz = a.z - b.z
  return dx * dx + dy * dy + dz * dz
}
export const dist = (a: BodyPt3, b: BodyPt3): number => Math.sqrt(distSq(a, b))
export const lerp = (a: BodyPt3, b: BodyPt3, t: number): BodyPt3 => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
  z: a.z + (b.z - a.z) * t,
})
export function norm(a: BodyPt3): BodyPt3 {
  const l = len(a)
  return l > 0 ? scale(a, 1 / l) : { x: 0, y: 0, z: 0 }
}

/** signed distance of p from the plane (positive on the +n / front side) */
export const planeDist = (pl: BodyPlane, p: BodyPt3): number => dot(pl.n, p) - pl.d

/** Newell's method over an ordered ring — robust for near-degenerate polygons.
 *  Returns null when the ring is degenerate (collinear / too small). */
export function planeFromPoints(pts: BodyPt3[]): BodyPlane | null {
  if (pts.length < 3) return null
  let nx = 0, ny = 0, nz = 0
  let cx = 0, cy = 0, cz = 0
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i]
    const q = pts[(i + 1) % pts.length]
    nx += (p.y - q.y) * (p.z + q.z)
    ny += (p.z - q.z) * (p.x + q.x)
    nz += (p.x - q.x) * (p.y + q.y)
    cx += p.x; cy += p.y; cz += p.z
  }
  const n = { x: nx, y: ny, z: nz }
  const l = len(n)
  if (l < AREA_EPS) return null
  const un = scale(n, 1 / l)
  const centroid = { x: cx / pts.length, y: cy / pts.length, z: cz / pts.length }
  return { n: un, d: dot(un, centroid) }
}

/** plane through two non-collinear directions at a point (healing's candidate planes) */
export function planeFromPointDirs(p: BodyPt3, d1: BodyPt3, d2: BodyPt3): BodyPlane | null {
  const n = cross(d1, d2)
  const l = len(n)
  if (l < 1e-9) return null
  // reject near-collinear pairs (the cross magnitude relative to the inputs)
  const l1 = len(d1), l2 = len(d2)
  if (l1 < 1e-9 || l2 < 1e-9) return null
  if (l / (l1 * l2) < 1e-4) return null
  const un = scale(n, 1 / l)
  return { n: un, d: dot(un, p) }
}

/** canonicalize so that ±n describe the same plane record (healing dedupe) */
export function canonicalPlane(pl: BodyPlane): BodyPlane {
  const { n } = pl
  const flip =
    n.z < -1e-9 ||
    (Math.abs(n.z) <= 1e-9 && (n.y < -1e-9 || (Math.abs(n.y) <= 1e-9 && n.x < 0)))
  return flip ? { n: scale(n, -1), d: -pl.d } : pl
}

export function samePlane(a: BodyPlane, b: BodyPlane, tol = WELD_TOL): boolean {
  const ca = canonicalPlane(a)
  const cb = canonicalPlane(b)
  return dot(ca.n, cb.n) > 0.999999 && Math.abs(ca.d - cb.d) <= tol
}

/** An orthonormal in-plane frame: (u, v, n) right-handed, so CCW in (u,v) = CCW about n. */
export interface PlaneFrame {
  o: BodyPt3
  u: BodyPt3
  v: BodyPt3
  n: BodyPt3
}

export function frameForPlane(pl: BodyPlane): PlaneFrame {
  const n = pl.n
  // pick the world axis least aligned with n to seed u
  const seed =
    Math.abs(n.x) <= Math.abs(n.y) && Math.abs(n.x) <= Math.abs(n.z)
      ? v3(1, 0, 0)
      : Math.abs(n.y) <= Math.abs(n.z)
        ? v3(0, 1, 0)
        : v3(0, 0, 1)
  const u = norm(cross(seed, n))
  const v = cross(n, u) // (u, v, n) right-handed: u × v = n
  return { o: scale(n, pl.d), u, v, n }
}

export interface Pt2 { x: number; y: number }

export const to2 = (f: PlaneFrame, p: BodyPt3): Pt2 => {
  const r = sub(p, f.o)
  return { x: dot(r, f.u), y: dot(r, f.v) }
}
export const from2 = (f: PlaneFrame, p: Pt2): BodyPt3 => add(f.o, add(scale(f.u, p.x), scale(f.v, p.y)))

/** distance from point to segment, plus the clamped parameter of the projection */
export function pointSegDist(p: BodyPt3, a: BodyPt3, b: BodyPt3): { d: number; t: number } {
  const ab = sub(b, a)
  const l2 = dot(ab, ab)
  if (l2 < 1e-18) return { d: dist(p, a), t: 0 }
  let t = dot(sub(p, a), ab) / l2
  t = Math.max(0, Math.min(1, t))
  return { d: dist(p, lerp(a, b, t)), t }
}

export interface SegSegResult {
  /** minimum distance between the segments */
  d: number
  /** parameters of the closest points on each segment, clamped to [0,1] */
  s: number
  t: number
  /** true when the segments are (near-)parallel — closest params are then degenerate */
  parallel: boolean
}

/** closest points between two 3D segments (standard clamped quadratic) */
export function segSegClosest(p1: BodyPt3, q1: BodyPt3, p2: BodyPt3, q2: BodyPt3): SegSegResult {
  const d1 = sub(q1, p1)
  const d2 = sub(q2, p2)
  const r = sub(p1, p2)
  const a = dot(d1, d1)
  const e = dot(d2, d2)
  const f = dot(d2, r)
  let s: number
  let t: number
  let parallel = false
  if (a < 1e-18 && e < 1e-18) { s = 0; t = 0 }
  else if (a < 1e-18) { s = 0; t = Math.max(0, Math.min(1, f / e)) }
  else {
    const c = dot(d1, r)
    if (e < 1e-18) { t = 0; s = Math.max(0, Math.min(1, -c / a)) }
    else {
      const b = dot(d1, d2)
      const denom = a * e - b * b
      // near-parallel test scaled to the segment lengths
      parallel = denom < 1e-9 * a * e
      s = parallel ? 0 : Math.max(0, Math.min(1, (b * f - c * e) / denom))
      t = (b * s + f) / e
      if (t < 0) { t = 0; s = Math.max(0, Math.min(1, -c / a)) }
      else if (t > 1) { t = 1; s = Math.max(0, Math.min(1, (b - c) / a)) }
    }
  }
  const c1 = lerp(p1, q1, s)
  const c2 = lerp(p2, q2, t)
  return { d: dist(c1, c2), s, t, parallel }
}
